import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Dependency-free, accessible checkbox.
 *
 * Backoffice has no `@radix-ui/react-checkbox` dependency and we do NOT add one
 * unprompted (CLAUDE.md §14 — ask the team before a new dep). This is a
 * controlled `<button role="checkbox">` styled with `cn`, rendering a lucide
 * icon (Check / Minus for indeterminate) — never an emoji (§3.8). If the team
 * later prefers the Radix primitive, that's a one-line dep add + swap.
 */
export interface CheckboxProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  indeterminate?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
}

export function Checkbox({
  checked,
  onCheckedChange,
  indeterminate = false,
  disabled = false,
  className,
  ...rest
}: CheckboxProps) {
  const active = checked || indeterminate;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={rest['aria-label']}
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        onCheckedChange(!checked);
      }}
      className={cn(
        'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-input ring-offset-background transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        active && 'border-primary bg-primary text-primary-foreground',
        className,
      )}
    >
      {indeterminate ? (
        <Minus className="h-3 w-3" />
      ) : checked ? (
        <Check className="h-3 w-3" />
      ) : null}
    </button>
  );
}
